import * as React from 'react';
import { useCallback } from 'react';
import { css } from '@emotion/core';
import LocalDiv from '../../localization/LocalDiv';
import { FLEX_ROW, FLEX_ADAPTIVE } from '../../layout/flexBox';
import { Link, useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { onMobileDevice, HIDE_ON_MOBILE } from '../../globalStyle/mediaQueries';
import { COLORS } from '../../globalStyle/colors';
import { Routes } from '../../navigation/routes';
import { DevPlanItem } from './models';
import Spacer from '../../layout/Spacer';
import { useDispatch } from 'react-redux';
import { useToggle } from '../../core/hooks/useToggle';
import { deleteDevPlanItemAction } from './api/deleteDevPlanItemAction';
import ConfirmationModal from '../../layout/modal/ConfirmationModal';
import { Str } from '../../localization/translationKeys';
import Msg from '../../localization/Msg';
import { btnStyle } from '../../forms/styles/button';
import { useMsgText } from '../../localization/hooks';
import { useAppState } from '../../core/redux/hooks';


const styles = {
  root:           [
    FLEX_ADAPTIVE,
    css`
      align-items: center;
      justify-content: space-between;
      
      ${onMobileDevice} {
        align-items: flex-start;
        justify-content: stretch;
      }
    `,
  ],
  titleContainer: [
    FLEX_ROW,
    css`
      align-items: baseline;
      color: ${COLORS.DARK_GREY};
        
      ${onMobileDevice} {
        align-self: flex-start;
      }
    `,
  ],
  title:          css`
    font-weight: normal;
    font-size: 26px;
    margin: 0;
  `,
  titleLink:      css`
    text-decoration: none;
    color: ${COLORS.DARK_GREY};

    &:hover { 
      opacity: .6;
      cursor:pointer;
    }
  `, 
  deleteBtn:      css`
    border: none;
    background-color: transparent;
    text-decoration: none;
    color: ${COLORS.DARK_GREY};
    
    &:hover { 
      opacity: .6;
      cursor:pointer;
    }
  `,
  deleteIcon:     css`
    font-size: 16px;
    margin-bottom: 2px;
  `,
  resourcesLink:  [
    btnStyle.primary(COLORS.DARK_GREY),
    css`
      min-width: 205px;
      ${HIDE_ON_MOBILE};
    `,
  ],
};


type Props = {
  participantId: number;
  projectId: number;
  item: DevPlanItem;
  routes: Routes;
  showResourcesLink: boolean;
  isParticipant: boolean;
  className?: string;
};


/**
 * DevItemHeader: a component that renders the Dev Plan Item Header which shows the item's title, gives an option to
 * delete the item, and gives a link to the development resources.
 */
const DevItemHeader: React.FC<Props> = props => {
  const history  = useHistory();
  const dispatch = useDispatch();

  const deleteConfirmationMsg = useMsgText(Str.ConfirmRemoveItem, '', { 'itemTitle': props.item.title });

  const deleteItemLabel = useMsgText(Str.DeleteDevItem);

  const [showDeleteConfirmation, toggleShowDeleteConfirmation] = useToggle(false);

  const hasDevResources = useAppState(state => !!state.devResources?.length);

  const devPlanProjectId = useAppState(state => state.projectSummary?.devPlanProjectId || props.projectId);

  const onConfirmDelete = useCallback(
    () => {
      dispatch(deleteDevPlanItemAction(props.participantId, devPlanProjectId, props.item.devItemId));
      toggleShowDeleteConfirmation();
      history.replace(props.routes.DevPlan);
    },
    [props, devPlanProjectId, toggleShowDeleteConfirmation, dispatch, history],
  );

  return (
    <LocalDiv css={styles.root} className={props.className}>
      <LocalDiv css={styles.titleContainer}>
        <Link to={props.routes.EditPlanItem} css={styles.titleLink}>
          <h1 css={styles.title}>{props.item.title}</h1>
        </Link>
        <Spacer sizePx={15} />
        { props.isParticipant && (
            <button onClick={toggleShowDeleteConfirmation} css={styles.deleteBtn} aria-label={deleteItemLabel}>
              <FontAwesomeIcon icon={faTrash} css={styles.deleteIcon} />
            </button>
          )
        }
      </LocalDiv>

      {
        hasDevResources && props.showResourcesLink && (
          <Link to={props.routes.DevResources_DevPlan} css={styles.resourcesLink}>
            <Msg str={Str.DevResources} />
          </Link>
        )
      }

      { // Delete Confirmation Modal
        showDeleteConfirmation && (
          <ConfirmationModal
            confirmationTxt={deleteConfirmationMsg}
            primaryActionStr={Str.No}
            secondaryActionStr={Str.Yes}
            onSelectPrimary={toggleShowDeleteConfirmation}
            onSelectSecondary={onConfirmDelete}
            onDismiss={toggleShowDeleteConfirmation}
          />
        )
      }
    </LocalDiv>
  );
};

export default DevItemHeader;
